export const HEATMAP_FILL_OPACITY = 0.45

export const specHeatmapKey = (specId: number, spellId: number) =>
  `spec:${specId}:${spellId}`

export const classHeatmapKey = (classId: number, spellId: number) =>
  `class:${classId}:${spellId}`

export type HeatmapSegment = {
  start: number
  end: number
  value: number
}

export const heatmapPhaseKey = (phaseId: string) => {
  const parts = phaseId.split('|')
  return parts.length >= 3 ? parts.slice(2).join('|') : phaseId
}

export const projectCachedHeatmap = ({
  percentagesByPhaseId,
  phases,
  phaseStarts,
  effectivePhaseDurations,
}: {
  percentagesByPhaseId: Record<string, number[]>
  phases: Array<{ id: string }>
  phaseStarts: number[]
  effectivePhaseDurations: number[]
}): HeatmapSegment[] => {
  const byKey = Object.entries(percentagesByPhaseId).reduce<
    Record<string, number[]>
  >((acc, [phaseId, values]) => {
    acc[heatmapPhaseKey(phaseId)] = values
    return acc
  }, {})
  const phaseIndicesInPlanOrder = phases
    .map((_, index) => index)
    .sort((left, right) => (phaseStarts[left] ?? 0) - (phaseStarts[right] ?? 0))

  const segments: HeatmapSegment[] = []
  phaseIndicesInPlanOrder.forEach((phaseIndex) => {
    const percentages = byKey[heatmapPhaseKey(phases[phaseIndex].id)]
    if (!percentages) return
    const phaseStart = phaseStarts[phaseIndex] ?? 0
    const phaseDuration = Math.max(
      0,
      Math.floor(effectivePhaseDurations[phaseIndex] ?? 0),
    )
    const length = Math.min(phaseDuration, percentages.length)

    let offset = 0
    while (offset < length) {
      const percentage = percentages[offset] ?? 0
      let end = offset + 1
      while (end < length && (percentages[end] ?? 0) === percentage) end += 1
      if (percentage > 0) {
        segments.push({
          start: phaseStart + offset,
          end: phaseStart + end,
          value: percentage / 100,
        })
      }
      offset = end
    }
  })
  return segments
}

export const heatmapValueAt = (segments: HeatmapSegment[], time: number) => {
  let low = 0
  let high = segments.length - 1
  while (low <= high) {
    const mid = (low + high) >> 1
    const segment = segments[mid]
    if (!segment) return 0
    if (time < segment.start) high = mid - 1
    else if (time >= segment.end) low = mid + 1
    else return segment.value
  }
  return 0
}

const heatmapPeak = (segments: HeatmapSegment[]) =>
  segments.reduce((peak, segment) => Math.max(peak, segment.value), 0)

// Height is relative to the row's own peak so the busiest moment for a spell
// always fills the row; the hover readout keeps showing the absolute share.
const heatmapHeightFraction = (value: number, peak: number) =>
  peak > 0 ? Math.max(0, Math.min(1, value / peak)) : 0

type HeatmapPathInput = {
  segments: HeatmapSegment[]
  rangeStart: number
  rangeEnd: number
  toX: (time: number) => number
  baseline: number
  height: number
}

export const buildHeatmapPaths = (
  input: HeatmapPathInput,
): { area: string; outline: string } | null => {
  const curves = buildHeatmapCurves(input)
  if (!curves) return null
  const format = (value: number) => value.toFixed(2)
  const baseline = format(input.baseline)
  return {
    area: curves
      .map(
        ({ start, end, curve }) =>
          `M${format(start.x)} ${baseline}L${format(start.x)} ${format(
            start.y,
          )}${curve}L${format(end.x)} ${baseline}Z`,
      )
      .join(''),
    outline: curves
      .map(
        ({ start, curve }) => `M${format(start.x)} ${format(start.y)}${curve}`,
      )
      .join(''),
  }
}

const buildHeatmapCurves = ({
  segments,
  rangeStart,
  rangeEnd,
  toX,
  baseline,
  height,
}: HeatmapPathInput) => {
  if (rangeEnd <= rangeStart || height <= 0) return null

  const ceiling = baseline - height
  const peak = heatmapPeak(segments)
  const toY = (value: number) =>
    baseline - heatmapHeightFraction(value, peak) * height
  const visible = segments.flatMap((segment) => {
    const start = Math.max(segment.start, rangeStart)
    const end = Math.min(segment.end, rangeEnd)
    if (end <= start || segment.value <= 0) return []
    return [
      {
        start,
        end,
        y: toY(segment.value),
        clippedLeft: segment.start < rangeStart,
        clippedRight: segment.end > rangeEnd,
      },
    ]
  })
  if (visible.length === 0) return null

  const groups: Array<typeof visible> = []
  visible.forEach((segment, index) => {
    const previous = visible[index - 1]
    if (previous && previous.end === segment.start) {
      groups[groups.length - 1]?.push(segment)
    } else {
      groups.push([segment])
    }
  })

  const clampY = (value: number) => Math.max(ceiling, Math.min(baseline, value))
  const format = (value: number) => value.toFixed(2)

  return groups.map((group) => {
    const first = group[0]
    const last = group[group.length - 1]
    const points: Array<{ x: number; y: number }> = [
      { x: toX(first.start), y: first.clippedLeft ? first.y : baseline },
    ]
    group.forEach((segment) => {
      if (segment.end - segment.start > 1) {
        points.push({ x: toX(segment.start + 0.5), y: segment.y })
        points.push({ x: toX(segment.end - 0.5), y: segment.y })
      } else {
        points.push({
          x: toX((segment.start + segment.end) / 2),
          y: segment.y,
        })
      }
    })
    points.push({
      x: toX(last.end),
      y: last.clippedRight ? last.y : baseline,
    })

    let curve = ''
    for (let index = 0; index < points.length - 1; index += 1) {
      const p0 = points[index - 1] ?? points[index]
      const p1 = points[index]
      const p2 = points[index + 1]
      const p3 = points[index + 2] ?? p2
      const c1x = p1.x + (p2.x - p0.x) / 6
      const c1y = clampY(p1.y + (p2.y - p0.y) / 6)
      const c2x = p2.x - (p3.x - p1.x) / 6
      const c2y = clampY(p2.y - (p3.y - p1.y) / 6)
      curve += `C${format(c1x)} ${format(c1y)} ${format(c2x)} ${format(
        c2y,
      )} ${format(p2.x)} ${format(p2.y)}`
    }
    return {
      start: points[0],
      end: points[points.length - 1],
      curve,
    }
  })
}
